<template>
  <div
    class="box-border w-full bg-white rounded-[16px] p-4 border border-black/[0.08] shadow-none flex flex-col gap-3 relative transition-all"
    @click="emitView('main')"
  >
    <!-- Top Row: Student Name + Status Badge + 3-Dot Kebab Menu -->
    <div class="flex items-center justify-between gap-2">
      <!-- Learner Name -->
      <div class="min-w-0 flex-1 flex items-center gap-2">
        <h3 class="font-['Raleway',sans-serif] text-[15px] font-bold text-[#043f42] truncate leading-tight">
          {{ studentFullName }}
        </h3>
      </div>

      <!-- Right: Status Badge & 3-Dot Menu -->
      <div
        class="flex items-center gap-2 shrink-0"
        @click.stop
      >
        <!-- Status Pill -->
        <div :class="['box-border w-fit h-fit flex flex-row gap-[5px] px-[8px] py-[3px] justify-center items-center rounded-full', statusBgClass]">
          <div :class="['box-border w-[6px] shrink-0 h-[6px] rounded-full overflow-hidden', statusDotClass]" />
          <span class="text-[10px] font-['Raleway',sans-serif] font-medium text-[#043f42] whitespace-nowrap">
            {{ statusText }}
          </span>
        </div>

        <!-- 3-Dot Kebab Context Menu Button -->
        <div
          ref="menuContainerRef"
          class="relative"
        >
          <button
            type="button"
            class="w-7 h-7 rounded-full flex items-center justify-center hover:bg-gray-100 text-[#043f42] cursor-pointer transition-colors focus:outline-none"
            title="Options"
            aria-label="Submission options"
            @click.stop="toggleMenu"
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <circle
                cx="12"
                cy="5"
                r="1"
              />
              <circle
                cx="12"
                cy="12"
                r="1"
              />
              <circle
                cx="12"
                cy="19"
                r="1"
              />
            </svg>
          </button>

          <!-- Dropdown Context Menu -->
          <div
            v-if="isMenuOpen"
            class="absolute right-0 top-full mt-1.5 w-52 max-w-[calc(100vw-32px)] bg-white rounded-[14px] shadow-2xl border border-black/10 py-1.5 z-50 overflow-hidden font-['Raleway',sans-serif]"
            @click.stop
          >
            <!-- View Submission -->
            <button
              type="button"
              class="w-full text-left px-3.5 py-2 text-[13px] text-[#043f42] hover:bg-gray-50 flex items-center gap-2.5 transition-colors cursor-pointer"
              @click="handleMenuAction('view')"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-[#01d680] shrink-0"
              >
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                <circle
                  cx="12"
                  cy="12"
                  r="3"
                />
              </svg>
              <span>View Submission</span>
            </button>

            <!-- Attachments (opens review modal linked directly to attachments tab if they exist; grayed out if none) -->
            <button
              type="button"
              class="w-full text-left px-3.5 py-2 text-[13px] flex items-center justify-between gap-2 transition-colors"
              :class="hasAttachments ? 'text-[#043f42] hover:bg-gray-50 cursor-pointer' : 'text-gray-400 opacity-50 cursor-not-allowed'"
              :disabled="!hasAttachments"
              @click="hasAttachments && handleMenuAction('attachments')"
            >
              <div class="flex items-center gap-2.5 truncate">
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  :class="hasAttachments ? 'text-[#01d680]' : 'text-gray-400'"
                  class="shrink-0"
                >
                  <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                </svg>
                <span class="truncate">Attachments</span>
              </div>
              <span
                v-if="hasAttachments"
                class="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-[#e6fbf2] text-[#01d680] shrink-0"
              >
                {{ attachmentsList.length }}
              </span>
              <span
                v-else
                class="text-[10px] text-gray-400 shrink-0"
              >None</span>
            </button>

            <!-- Resend Email -->
            <button
              type="button"
              class="w-full text-left px-3.5 py-2 text-[13px] text-[#043f42] hover:bg-gray-50 flex items-center gap-2.5 transition-colors cursor-pointer"
              @click="handleMenuAction('resend')"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="text-[#043f42] shrink-0"
              >
                <path d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
              </svg>
              <span>Resend Email</span>
            </button>

            <!-- Download Documents -->
            <button
              type="button"
              class="w-full text-left px-3.5 py-2 text-[13px] flex items-center gap-2.5 transition-colors"
              :class="canDownload ? 'text-[#043f42] hover:bg-gray-50 cursor-pointer' : 'text-gray-400 opacity-60 cursor-not-allowed'"
              @click="handleMenuAction('download')"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                :class="canDownload ? 'text-[#043f42]' : 'text-gray-400'"
                class="shrink-0"
              >
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                <polyline points="7 10 12 15 17 10" />
                <line
                  x1="12"
                  y1="15"
                  x2="12"
                  y2="3"
                />
              </svg>
              <span>Download Documents</span>
            </button>

            <!-- Divider -->
            <div class="my-1 border-t border-gray-100" />

            <!-- Delete -->
            <button
              type="button"
              class="w-full text-left px-3.5 py-2 text-[13px] flex items-center gap-2.5 transition-colors"
              :class="canDelete ? 'text-red-600 hover:bg-red-50 cursor-pointer' : 'text-gray-400 opacity-40 cursor-not-allowed'"
              :disabled="!canDelete"
              @click="canDelete && handleMenuAction('delete')"
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                class="shrink-0"
                :class="canDelete ? 'text-red-600' : 'text-gray-400'"
              >
                <polyline points="3 6 5 6 21 6" />
                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              </svg>
              <span>Delete</span>
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Email Line (with inline editing support) -->
    <div
      class="flex items-center gap-2 text-[13px] text-[#5b5c60] font-['Raleway',sans-serif]"
      @click.stop
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="text-gray-400 shrink-0"
      >
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
        <polyline points="22,6 12,13 2,6" />
      </svg>

      <div
        v-if="isEditingEmail"
        class="flex items-center gap-2 flex-1"
        @click.stop
      >
        <input
          ref="emailInputRef"
          v-model="editEmailValue"
          type="email"
          class="border rounded px-2 py-0.5 text-xs w-full outline-none focus:ring-1 focus:ring-[#043f42]"
          @keyup.enter="saveEmail"
          @keyup.esc="cancelEditEmail"
        >
        <button
          class="text-[#01d680] hover:text-[#043f42] text-xs font-bold shrink-0 cursor-pointer"
          @click="saveEmail"
        >
          Save
        </button>
        <button
          class="text-red-500 hover:text-red-700 text-xs shrink-0 cursor-pointer"
          @click="cancelEditEmail"
        >
          Cancel
        </button>
      </div>

      <div
        v-else
        class="flex items-center gap-1.5 min-w-0 flex-1 cursor-pointer group"
        title="Click to edit email"
        @click.stop="startEditEmail"
      >
        <span
          class="sm:hidden truncate group-hover:text-[#01d680] transition-colors"
          :title="enrollment.student_email"
        >{{ mobileTruncatedEmail }}</span>
        <span
          class="hidden sm:inline truncate group-hover:text-[#01d680] transition-colors"
          :title="enrollment.student_email"
        >{{ enrollment.student_email || 'No email provided' }}</span>
        <button
          type="button"
          class="text-gray-400 group-hover:text-[#043f42] cursor-pointer shrink-0 p-1 -m-1 transition-colors"
          title="Edit Email"
          @click.stop="startEditEmail"
        >
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          >
            <path d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.863 4.487zm0 0L19.5 7.125" />
          </svg>
        </button>
      </div>
    </div>

    <!-- Date Line & Attachments Preview Line -->
    <div class="flex items-center justify-between text-[12px] text-[#5b5c60] font-['Raleway',sans-serif] pt-1 border-t border-gray-50">
      <div class="flex items-center gap-1.5">
        <svg
          width="13"
          height="13"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-gray-400 shrink-0"
        >
          <rect
            x="3"
            y="4"
            width="18"
            height="18"
            rx="2"
            ry="2"
          />
          <line
            x1="16"
            y1="2"
            x2="16"
            y2="6"
          />
          <line
            x1="8"
            y1="2"
            x2="8"
            y2="6"
          />
          <line
            x1="3"
            y1="10"
            x2="21"
            y2="10"
          />
        </svg>
        <span>{{ dateLabel }}: <strong class="text-[#043f42] font-semibold">{{ dateValue }}</strong></span>
      </div>

      <!-- Attachments Link if any -->
      <button
        v-if="hasAttachments"
        type="button"
        class="flex items-center gap-1 text-[11px] font-semibold text-[#02949b] hover:text-[#01d680] transition-colors cursor-pointer group"
        @click.stop="emitView('attachments')"
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="shrink-0 text-[#02949b] group-hover:text-[#01d680] transition-colors"
        >
          <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
        </svg>
        <span class="underline underline-offset-2">{{ attachmentsList.length }} attachment{{ attachmentsList.length > 1 ? 's' : '' }}</span>
      </button>
      <span
        v-else
        class="text-[11px] text-gray-400 opacity-60 cursor-default"
      >No attachments</span>
    </div>

    <!-- Resend Confirmation Modal -->
    <Teleport to="body">
      <div
        v-if="isResendModalOpen"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 font-['Raleway',sans-serif]"
        @click.self="closeResendModal"
      >
        <div class="bg-white rounded-[20px] max-w-sm w-full p-6 shadow-xl relative animate-in fade-in zoom-in-95 duration-200">
          <h3 class="text-lg font-bold text-[#043f42] mb-2">
            Resend Signature Request
          </h3>
          <p class="text-sm text-gray-600 mb-6">
            Are you sure you want to resend the signing invitation email to <strong class="text-black">{{ enrollment.student_email }}</strong>?
          </p>
          <div class="flex justify-end gap-3">
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
              @click="closeResendModal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="px-4 py-2 text-sm font-medium text-white bg-[#043f42] hover:bg-[#032e30] rounded-full transition-colors cursor-pointer"
              @click="confirmResend"
            >
              Resend Email
            </button>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { computed, ref, onMounted, onUnmounted, nextTick } from 'vue'

const props = defineProps({
  enrollment: {
    type: Object,
    required: true
  }
})

const isMenuOpen = ref(false)
const isResendModalOpen = ref(false)
const menuContainerRef = ref(null)

const isEditingEmail = ref(false)
const editEmailValue = ref('')
const emailInputRef = ref(null)

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value
}

const closeMenu = () => {
  isMenuOpen.value = false
}

const studentFullName = computed(() => {
  return `${props.enrollment.name || ''} ${props.enrollment.surname || ''}`.trim() || 'Unknown'
})

const mobileTruncatedEmail = computed(() => {
  const email = props.enrollment?.student_email || ''
  if (!email) return 'No email provided'
  if (email.length > 15) {
    return email.substring(0, 15) + '...'
  }
  return email
})

const studentSubmitter = computed(() => {
  const submission = props.enrollment.submission
  if (!submission) return null
  const email = props.enrollment.student_email || ''
  const submitters = submission?.submitters || []
  return submitters.find(s => s.uuid?.toLowerCase() === 'student') ||
         submitters.find(s => {
           const isSponsor = s.uuid?.toLowerCase().includes('sponsor') || s.name?.toLowerCase().includes('sponsor')
           const isTrainer = s.uuid?.toLowerCase().includes('trainer') || s.name?.toLowerCase().includes('trainer')
           return !isSponsor && !isTrainer && s.email === email
         }) || submitters[0]
})

const attachmentsList = computed(() => {
  if (Array.isArray(props.enrollment.student_uploaded_documents)) {
    return props.enrollment.student_uploaded_documents
  }
  const docs = props.enrollment.submission?.uploaded_documents || []
  if (!Array.isArray(docs)) return []
  const studentId = studentSubmitter.value?.id
  const studentUuid = studentSubmitter.value?.uuid || 'student'
  return docs.filter(doc => {
    if (doc.submitter_id && studentId) return doc.submitter_id === studentId
    if (doc.submitter_uuid) return doc.submitter_uuid === studentUuid || doc.submitter_uuid.toLowerCase() === 'student'
    return false
  })
})

const hasAttachments = computed(() => {
  return attachmentsList.value.length > 0
})

const canDelete = computed(() => {
  return statusText.value !== 'Completed' && statusText.value !== 'Declined'
})

const canDownload = computed(() => {
  const submitters = props.enrollment.submission?.submitters || []
  if (submitters.length === 0) return false
  return submitters.every(s => !!s.completed_at)
})

const statusText = computed(() => {
  if (studentSubmitter.value) {
    const deliveryStatus = studentSubmitter.value.metadata?.email_delivery_status
    if (deliveryStatus === 'complained' || studentSubmitter.value.metadata?.complained_at) return 'Spam Complaint'
    if (deliveryStatus === 'bounced' || studentSubmitter.value.metadata?.bounced_at) return 'Email Bounced'
    if (studentSubmitter.value.declined_at) return 'Declined'
    if (studentSubmitter.value.completed_at) return 'Completed'
    if (studentSubmitter.value.started_at) return 'In progress'
    if (studentSubmitter.value.sent_at) {
      if (studentSubmitter.value.started_at) return 'In progress'
      if (studentSubmitter.value.opened_at) return 'Opened'
      if (deliveryStatus === 'delivered' || studentSubmitter.value.metadata?.delivered_at) return 'Delivered'
      return 'Sent'
    }
    return 'Not sent'
  }
  return 'Not sent'
})

const statusBgClass = computed(() => {
  const text = statusText.value
  if (text === 'Completed') return 'bg-[#01d680ff]'
  if (text === 'Declined') return 'bg-[#fee2e2]'
  if (text === 'In progress') return 'bg-[#fed7aa]'
  if (text === 'Opened') return 'bg-[#bae6fd]'
  if (text === 'Delivered') return 'bg-[#f3e8ff]'
  if (text === 'Sent') return 'bg-[#e2e8f0]'
  if (text === 'Email Bounced') return 'bg-[#fee2e2]'
  if (text === 'Spam Complaint') return 'bg-[#ffedd5]'
  return 'bg-[#f3f4f6]'
})

const statusDotClass = computed(() => {
  const text = statusText.value
  if (text === 'Completed') return 'bg-[#043f42ff]'
  if (text === 'Declined') return 'bg-[#dc2626]'
  if (text === 'In progress') return 'bg-[#ea580c]'
  if (text === 'Opened') return 'bg-[#0284c7]'
  if (text === 'Delivered') return 'bg-[#9333ea]'
  if (text === 'Sent') return 'bg-[#64748b]'
  if (text === 'Email Bounced') return 'bg-[#dc2626]'
  if (text === 'Spam Complaint') return 'bg-[#ea580c]'
  return 'bg-[#9ca3af]'
})

const dateLabel = computed(() => {
  if (studentSubmitter.value) {
    if (studentSubmitter.value.completed_at) return 'Completed'
    if (studentSubmitter.value.opened_at) return 'Opened'
    if (studentSubmitter.value.metadata?.delivered_at) return 'Delivered'
    if (studentSubmitter.value.sent_at) return 'Sent'
  }
  return 'Created'
})

const dateValue = computed(() => {
  let dateToFormat = null
  if (studentSubmitter.value) {
    dateToFormat = studentSubmitter.value.completed_at ||
                   studentSubmitter.value.opened_at ||
                   studentSubmitter.value.metadata?.delivered_at ||
                   studentSubmitter.value.sent_at ||
                   studentSubmitter.value.created_at
  }
  if (!dateToFormat) return 'N/A'
  const d = new Date(dateToFormat)
  return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
})

const emitView = (tab = 'main') => {
  const event = new CustomEvent('programme-action', {
    detail: {
      action: 'view_submission',
      enrollment_id: props.enrollment.id,
      submitter_id: studentSubmitter.value?.id,
      tab
    },
    bubbles: true,
    composed: true
  })
  window.dispatchEvent(event)
}

const handleMenuAction = (action) => {
  closeMenu()
  if (action === 'view') {
    emitView('main')
  } else if (action === 'attachments') {
    emitView('attachments')
  } else if (action === 'resend') {
    if (!props.enrollment.submission?.id) return
    isResendModalOpen.value = true
  } else if (action === 'download') {
    if (canDownload.value) {
      const event = new CustomEvent('programme-action', {
        detail: { action: 'download', enrollment_id: props.enrollment.id },
        bubbles: true,
        composed: true
      })
      window.dispatchEvent(event)
    } else {
      const event = new CustomEvent('programme-action', {
        detail: {
          action: 'toast',
          message: 'Download will be available once all parties (Training Provider, Learner, and Sponsor) have signed.',
          type: 'info'
        },
        bubbles: true,
        composed: true
      })
      window.dispatchEvent(event)
    }
  } else if (action === 'delete') {
    const event = new CustomEvent('programme-action', {
      detail: { action: 'delete', enrollment_id: props.enrollment.id },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)
  }
}

const closeResendModal = () => {
  isResendModalOpen.value = false
}

const confirmResend = async () => {
  isResendModalOpen.value = false
  if (!studentSubmitter.value?.id) {
    const event = new CustomEvent('programme-action', {
      detail: { action: 'toast', message: 'Submitter ID not found.', type: 'error' },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)
    return
  }

  try {
    const csrfToken = document.querySelector("meta[name='csrf-token']")?.getAttribute('content')
    const res = await fetch(`/submissions/${props.enrollment.submission.id}/resend_email`, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': csrfToken,
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      body: JSON.stringify({ submitter_id: studentSubmitter.value.id })
    })
    if (res.ok) {
      const event = new CustomEvent('programme-action', {
        detail: { action: 'toast', message: 'Invitation resent successfully', type: 'success' },
        bubbles: true,
        composed: true
      })
      window.dispatchEvent(event)
    } else {
      const event = new CustomEvent('programme-action', {
        detail: { action: 'toast', message: 'Failed to resend email.', type: 'error' },
        bubbles: true,
        composed: true
      })
      window.dispatchEvent(event)
    }
  } catch (error) {
    console.error(error)
    const event = new CustomEvent('programme-action', {
      detail: { action: 'toast', message: 'An error occurred while resending the email.', type: 'error' },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)
  }
}

const startEditEmail = () => {
  editEmailValue.value = props.enrollment.student_email || ''
  isEditingEmail.value = true
  nextTick(() => {
    emailInputRef.value?.focus()
  })
}

const cancelEditEmail = () => {
  isEditingEmail.value = false
}

const saveEmail = async () => {
  if (!editEmailValue.value || editEmailValue.value.trim() === '') return
  try {
    const newEmail = editEmailValue.value
    const event = new CustomEvent('programme-action', {
      detail: { action: 'update_email', enrollment_id: props.enrollment.id, email: newEmail },
      bubbles: true,
      composed: true
    })
    window.dispatchEvent(event)
    if (studentSubmitter.value) {
      studentSubmitter.value.email = newEmail
    }
    props.enrollment.student_email = newEmail
    isEditingEmail.value = false
  } catch (error) {
    console.error(error)
  }
}

const handleClickOutside = (event) => {
  if (isMenuOpen.value && menuContainerRef.value && !menuContainerRef.value.contains(event.target)) {
    closeMenu()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
